// src/pages/hr/learning/CoursesTab.jsx
// ============================================================
// Learning & Development - Courses Catalog
// Redesigned with Green Theme & Outline Icons
// ============================================================

import React, { useState } from "react";
import {
  Plus,
  Search,
  Archive,
  Trash2,
  BookX,
  Clock,
  BookOpen,
} from "lucide-react";
import { EMS_DEPARTMENTS } from "../../../utils/hrConstants";

export default function CoursesTab({
  learningState,
  employees = [],
  departments = [],
  currentUser,
  onSelectCourse,
}) {
  const { data, createCourse, archiveCourse, deleteCourse } = learningState;
  const rawCourses = data?.courses || [];

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [showCreateModal, setShowCreateModal] = useState(false);

  // Delete confirmation dialog state
  const [deleteTarget, setDeleteTarget] = useState(null); // course object to delete

  // New Course Form State
  const [courseForm, setCourseForm] = useState({
    title: "",
    description: "",
    category: "Technical Skills",
    department: "All Departments",
    duration: "45 Mins",
    videoUrl: "",
    assignedToNames: [],
    quizQuestion: "What is the primary objective of this training?",
    quizOption1: "Improve execution efficiency",
    quizOption2: "Reduce team collaboration",
    quizOption3: "Bypass standard compliance",
    quizOption4: "Ignore security protocols",
    correctOption: 0,
  });

  const isAdminOrHR =
    currentUser?.role === "admin" || currentUser?.role === "hr";

  const filteredCourses = rawCourses.filter((c) => {
    const matchesCat = selectedCategory === "all" || c.category === selectedCategory;
    const matchesSearch =
      !searchTerm ||
      (c.title || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
      (c.description || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
      (c.department || "").toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCat && matchesSearch;
  });

  const handleCreateCourseSubmit = (e) => {
    e.preventDefault();
    createCourse(
      {
        title: courseForm.title,
        description: courseForm.description,
        category: courseForm.category,
        department: courseForm.department,
        duration: courseForm.duration,
        videoUrl: courseForm.videoUrl,
        assignedToNames:
          courseForm.assignedToNames.length > 0
            ? courseForm.assignedToNames
            : employees.map((e) => e.displayName),
        quiz: {
          passPercentage: 70,
          questions: [
            {
              id: "q1",
              question: courseForm.quizQuestion,
              options: [
                courseForm.quizOption1,
                courseForm.quizOption2,
                courseForm.quizOption3,
                courseForm.quizOption4,
              ],
              correctAnswer: Number(courseForm.correctOption),
            },
          ],
        },
      },
      currentUser
    );

    setShowCreateModal(false);
    setCourseForm({
      title: "",
      description: "",
      category: "Technical Skills",
      department: "All Departments",
      duration: "45 Mins",
      videoUrl: "",
      assignedToNames: [],
      quizQuestion: "What is the primary objective of this training?",
      quizOption1: "Improve execution efficiency",
      quizOption2: "Reduce team collaboration",
      quizOption3: "Bypass standard compliance",
      quizOption4: "Ignore security protocols",
      correctOption: 0,
    });
  };

  const handleConfirmDelete = () => {
    if (!deleteTarget) return;
    deleteCourse(deleteTarget.id, currentUser);
    setDeleteTarget(null);
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "1.25rem" }}>
      <style>{`
        .lnd-green-badge {
          background: #f0fdf4;
          color: #15803d;
          border: 1.5px solid #bbf7d0;
          padding: 0.2rem 0.6rem;
          border-radius: 99px;
          font-size: 0.72rem;
          font-weight: 700;
          display: inline-flex;
          align-items: center;
          gap: 0.25rem;
        }
        .btn-green-primary {
          background: #1b4b31;
          color: #ffffff;
          border: none;
          border-radius: 9px;
          padding: 0.5rem 1rem;
          font-weight: 700;
          font-size: 0.82rem;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          gap: 0.45rem;
          transition: all 0.18s ease;
        }
        .btn-green-primary:hover {
          background: #143724;
        }
        .btn-outline-action {
          background: #ffffff;
          border: 1.5px solid #cbd5e1;
          border-radius: 9px;
          padding: 0.45rem 0.85rem;
          font-weight: 700;
          font-size: 0.78rem;
          color: #475569;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          gap: 0.35rem;
          transition: all 0.18s ease;
        }
        .btn-outline-action:hover {
          background: #f8fafc;
          border-color: #94a3b8;
        }
      `}</style>

      {/* Top Filter & Creation Action Bar */}
      <div className="card" style={{ padding: "0.9rem 1.15rem", background: "#ffffff", borderRadius: 14, border: "1px solid #e2e8f0" }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: "1rem",
          }}
        >
          {/* Category Filter Pills */}
          <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
            {[
              { id: "all", label: "All Categories" },
              { id: "Technical Skills", label: "Technical Skills" },
              { id: "Compliance & Security", label: "Compliance & Security" },
              { id: "Leadership", label: "Leadership" },
              { id: "Soft Skills", label: "Soft Skills" },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setSelectedCategory(tab.id)}
                style={{
                  padding: "0.45rem 0.85rem",
                  fontSize: "0.8rem",
                  fontWeight: 700,
                  borderRadius: "8px",
                  border: selectedCategory === tab.id ? "1.5px solid #15803d" : "1.5px solid #e2e8f0",
                  cursor: "pointer",
                  background:
                    selectedCategory === tab.id ? "#f0fdf4" : "#ffffff",
                  color:
                    selectedCategory === tab.id ? "#15803d" : "#475569",
                  transition: "all 0.15s ease",
                }}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Search + Create Button */}
          <div style={{ display: "flex", gap: "0.75rem", alignItems: "center" }}>
            <div style={{ position: "relative", width: "230px" }}>
              <Search
                size={15}
                strokeWidth={1.8}
                style={{
                  position: "absolute",
                  left: "11px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  color: "#94a3b8",
                }}
              />
              <input
                type="text"
                placeholder="Search courses..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{
                  width: "100%",
                  padding: "0.48rem 0.6rem 0.48rem 2.1rem",
                  fontSize: "0.82rem",
                  borderRadius: "9px",
                  border: "1.5px solid #cbd5e1",
                  background: "#ffffff",
                  outline: "none",
                }}
              />
            </div>

            {(isAdminOrHR || currentUser?.role === "lead") && (
              <button
                className="btn-green-primary"
                onClick={() => setShowCreateModal(true)}
              >
                <Plus size={16} strokeWidth={2} /> Create Course
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Empty State */}
      {rawCourses.length === 0 && (
        <div
          className="card"
          style={{
            padding: "4rem 2rem",
            textAlign: "center",
            color: "#64748b",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "0.75rem",
            background: "#ffffff",
            borderRadius: 16,
            border: "2px dashed #cbd5e1",
          }}
        >
          <BookX size={44} strokeWidth={1.5} style={{ opacity: 0.35 }} />
          <div style={{ fontSize: "1.1rem", fontWeight: 800, color: "#334155" }}>
            No courses available
          </div>
          <div style={{ fontSize: "0.85rem", maxWidth: 380 }}>
            {isAdminOrHR || currentUser?.role === "lead"
              ? 'Click "Create Course" above to publish the first training module.'
              : "No courses have been published yet. Please check back later."}
          </div>
        </div>
      )}

      {/* No search results */}
      {rawCourses.length > 0 && filteredCourses.length === 0 && (
        <div
          className="card"
          style={{
            padding: "2.5rem 2rem",
            textAlign: "center",
            color: "#64748b",
            background: "#ffffff",
            borderRadius: 14,
            border: "1px solid #e2e8f0",
          }}
        >
          <div style={{ fontSize: "0.92rem", fontWeight: 700, color: "#334155" }}>
            No courses match your search or filter.
          </div>
        </div>
      )}

      {/* Course Cards Catalog */}
      {filteredCourses.length > 0 && (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(360px, 1fr))",
            gap: "1.25rem",
          }}
        >
          {filteredCourses.map((crs) => (
            <div
              key={crs.id}
              className="card"
              style={{
                padding: "1.35rem",
                display: "flex",
                flexDirection: "column",
                gap: "1rem",
                background: "#ffffff",
                borderRadius: 14,
                border: "1px solid #e2e8f0",
                borderTop: "4px solid #15803d",
                boxShadow: "0 2px 8px rgba(0,0,0,0.03)",
              }}
            >
              <div>
                {/* Category & Duration Row */}
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "0.6rem",
                  }}
                >
                  <span className="lnd-green-badge">
                    {crs.category}
                  </span>
                  <span
                    style={{ fontSize: "0.75rem", color: "#15803d", fontWeight: 700, display: "inline-flex", alignItems: "center", gap: "0.25rem" }}
                  >
                    <Clock size={13} strokeWidth={1.8} /> {crs.duration}
                  </span>
                </div>

                {/* Title & Description */}
                <h3
                  style={{
                    margin: "0 0 0.4rem 0",
                    fontSize: "1.08rem",
                    fontWeight: 800,
                    color: "#0f172a",
                  }}
                >
                  {crs.title}
                </h3>
                <p
                  style={{
                    margin: "0 0 0.85rem 0",
                    fontSize: "0.82rem",
                    color: "#64748b",
                    lineHeight: 1.45,
                  }}
                >
                  {crs.description}
                </p>

                {/* Video Thumbnail with Delete Overlay */}
                <div
                  style={{
                    position: "relative",
                    width: "100%",
                    height: "185px",
                    borderRadius: "10px",
                    overflow: "hidden",
                    marginBottom: "0.85rem",
                    background: "#000000",
                  }}
                >
                  <iframe
                    width="100%"
                    height="100%"
                    src={crs.embedUrl}
                    title={crs.title}
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    style={{ display: "block" }}
                  />

                  {/* Delete Overlay Button — Admin / HR only */}
                  {isAdminOrHR && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setDeleteTarget(crs);
                      }}
                      title="Delete this course"
                      style={{
                        position: "absolute",
                        top: "8px",
                        right: "8px",
                        zIndex: 10,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: "30px",
                        height: "30px",
                        borderRadius: "8px",
                        background: "rgba(220, 38, 38, 0.92)",
                        border: "none",
                        cursor: "pointer",
                        color: "#ffffff",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.35)",
                        backdropFilter: "blur(4px)",
                        transition: "background 0.15s ease, transform 0.15s ease",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = "rgba(185, 28, 28, 1)";
                        e.currentTarget.style.transform = "scale(1.1)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = "rgba(220, 38, 38, 0.92)";
                        e.currentTarget.style.transform = "scale(1)";
                      }}
                    >
                      <Trash2 size={14} strokeWidth={1.8} />
                    </button>
                  )}
                </div>

                {/* Course Meta: Dept & Assigned count */}
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    fontSize: "0.76rem",
                    color: "#475569",
                    padding: "0.45rem 0.75rem",
                    background: "#f8fafc",
                    border: "1px solid #e2e8f0",
                    borderRadius: "8px",
                  }}
                >
                  <span>
                    Dept: <strong style={{ color: "#0f172a" }}>{crs.department}</strong>
                  </span>
                  <span>
                    Assigned:{" "}
                    <strong style={{ color: "#15803d" }}>
                      {crs.assignedToNames?.length || 0} Employee
                      {crs.assignedToNames?.length !== 1 ? "s" : ""}
                    </strong>
                  </span>
                </div>
              </div>

              {/* Actions Row */}
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  borderTop: "1px solid #f1f5f9",
                  paddingTop: "0.75rem",
                }}
              >
                <span style={{ fontSize: "0.74rem", color: "#64748b", fontWeight: 500 }}>
                  By: {crs.assignedBy || crs.createdBy || "HR Admin"}
                </span>

                <div style={{ display: "flex", gap: "0.5rem" }}>
                  {isAdminOrHR && (
                    <button
                      className="btn-outline-action"
                      onClick={() => archiveCourse(crs.id, currentUser)}
                      title={crs.status === "Archived" ? "Unarchive" : "Archive"}
                      style={{ fontSize: "0.75rem", padding: "0.35rem 0.6rem" }}
                    >
                      <Archive size={14} strokeWidth={1.8} />
                    </button>
                  )}
                  <button
                    className="btn-green-primary"
                    onClick={() => onSelectCourse && onSelectCourse(crs)}
                    style={{ fontSize: "0.78rem", padding: "0.38rem 0.85rem" }}
                  >
                    View Details
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── DELETE CONFIRMATION DIALOG ──────────────────────────────────────── */}
      {deleteTarget && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.5)",
            backdropFilter: "blur(4px)",
            zIndex: 1200,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "1rem",
          }}
        >
          <div
            className="card"
            style={{
              width: "420px",
              maxWidth: "100%",
              padding: "1.75rem",
              display: "flex",
              flexDirection: "column",
              gap: "1.25rem",
              borderTop: "4px solid #dc2626",
              background: "#ffffff",
              borderRadius: 14,
            }}
          >
            {/* Icon & Heading */}
            <div style={{ display: "flex", alignItems: "flex-start", gap: "0.85rem" }}>
              <div
                style={{
                  width: "44px",
                  height: "44px",
                  borderRadius: "10px",
                  background: "#fef2f2",
                  border: "1px solid #fca5a5",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                  color: "#dc2626",
                }}
              >
                <Trash2 size={20} strokeWidth={1.8} />
              </div>
              <div>
                <div
                  style={{ fontSize: "1rem", fontWeight: 800, color: "#0f172a", marginBottom: "0.3rem" }}
                >
                  Delete this course?
                </div>
                <div style={{ fontSize: "0.83rem", color: "#64748b", lineHeight: 1.5 }}>
                  <strong style={{ color: "#0f172a" }}>{deleteTarget.title}</strong> will be
                  permanently removed, including its video, quiz, and all employee assignments.
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.75rem" }}>
              <button
                className="btn-outline-action"
                onClick={() => setDeleteTarget(null)}
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDelete}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "0.4rem",
                  padding: "0.5rem 1.1rem",
                  fontSize: "0.84rem",
                  fontWeight: 700,
                  background: "#dc2626",
                  color: "#ffffff",
                  border: "none",
                  borderRadius: "8px",
                  cursor: "pointer",
                }}
              >
                <Trash2 size={15} strokeWidth={1.8} /> Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── CREATE COURSE MODAL ──────────────────────────────────────────────── */}
      {showCreateModal && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.5)",
            backdropFilter: "blur(3px)",
            zIndex: 1100,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "1rem",
          }}
        >
          <div
            className="card"
            style={{
              width: "560px",
              maxWidth: "100%",
              padding: "1.75rem",
              maxHeight: "90vh",
              overflowY: "auto",
              background: "#ffffff",
              borderRadius: 16,
              boxShadow: "0 20px 25px -5px rgba(0,0,0,0.15)",
            }}
          >
            <h3 style={{ margin: "0 0 1.25rem 0", fontSize: "1.15rem", fontWeight: 800, color: "#0f172a" }}>
              Create New Video Course
            </h3>
            <form
              onSubmit={handleCreateCourseSubmit}
              style={{ display: "flex", flexDirection: "column", gap: "1rem" }}
            >
              <div>
                <label
                  style={{
                    display: "block",
                    fontSize: "0.8rem",
                    fontWeight: 700,
                    marginBottom: "0.3rem",
                    color: "#334155",
                  }}
                >
                  Course Title *
                </label>
                <input
                  required
                  type="text"
                  placeholder="e.g. AWS Cloud Security Best Practices"
                  value={courseForm.title}
                  onChange={(e) =>
                    setCourseForm({ ...courseForm, title: e.target.value })
                  }
                  style={{
                    width: "100%",
                    padding: "0.55rem 0.75rem",
                    borderRadius: "8px",
                    border: "1.5px solid #cbd5e1",
                    fontSize: "0.85rem",
                    outline: "none",
                  }}
                />
              </div>

              <div>
                <label
                  style={{
                    display: "block",
                    fontSize: "0.8rem",
                    fontWeight: 700,
                    marginBottom: "0.3rem",
                    color: "#334155",
                  }}
                >
                  Course Description
                </label>
                <textarea
                  rows={2}
                  placeholder="Brief description of training objectives..."
                  value={courseForm.description}
                  onChange={(e) =>
                    setCourseForm({ ...courseForm, description: e.target.value })
                  }
                  style={{
                    width: "100%",
                    padding: "0.55rem 0.75rem",
                    borderRadius: "8px",
                    border: "1.5px solid #cbd5e1",
                    fontSize: "0.85rem",
                    outline: "none",
                  }}
                />
              </div>

              <div
                style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.75rem" }}
              >
                <div>
                  <label
                    style={{
                      display: "block",
                      fontSize: "0.8rem",
                      fontWeight: 700,
                      marginBottom: "0.3rem",
                      color: "#334155",
                    }}
                  >
                    Category
                  </label>
                  <select
                    value={courseForm.category}
                    onChange={(e) =>
                      setCourseForm({ ...courseForm, category: e.target.value })
                    }
                    style={{
                      width: "100%",
                      padding: "0.55rem 0.75rem",
                      borderRadius: "8px",
                      border: "1.5px solid #cbd5e1",
                      fontSize: "0.85rem",
                      background: "#ffffff",
                    }}
                  >
                    <option value="Technical Skills">Technical Skills</option>
                    <option value="Compliance & Security">Compliance & Security</option>
                    <option value="Leadership">Leadership</option>
                    <option value="Soft Skills">Soft Skills</option>
                  </select>
                </div>

                <div>
                  <label
                    style={{
                      display: "block",
                      fontSize: "0.8rem",
                      fontWeight: 700,
                      marginBottom: "0.3rem",
                      color: "#334155",
                    }}
                  >
                    Department Target
                  </label>
                  <select
                    value={courseForm.department}
                    onChange={(e) =>
                      setCourseForm({ ...courseForm, department: e.target.value })
                    }
                    style={{
                      width: "100%",
                      padding: "0.55rem 0.75rem",
                      borderRadius: "8px",
                      border: "1.5px solid #cbd5e1",
                      fontSize: "0.85rem",
                      background: "#ffffff",
                    }}
                  >
                    <option value="All Departments">All Departments</option>
                    {(departments.length > 0 ? departments : EMS_DEPARTMENTS).map(
                      (d) => (
                        <option key={d} value={d}>
                          {d}
                        </option>
                      )
                    )}
                  </select>
                </div>
              </div>

              <div>
                <label
                  style={{
                    display: "block",
                    fontSize: "0.8rem",
                    fontWeight: 700,
                    marginBottom: "0.3rem",
                    color: "#334155",
                  }}
                >
                  YouTube Video Link (URL) *
                </label>
                <input
                  required
                  type="url"
                  placeholder="https://www.youtube.com/watch?v=VIDEO_ID"
                  value={courseForm.videoUrl}
                  onChange={(e) =>
                    setCourseForm({ ...courseForm, videoUrl: e.target.value })
                  }
                  style={{
                    width: "100%",
                    padding: "0.55rem 0.75rem",
                    borderRadius: "8px",
                    border: "1.5px solid #cbd5e1",
                    fontSize: "0.85rem",
                    outline: "none",
                  }}
                />
              </div>

              {/* Quiz Question Configuration Section */}
              <div
                style={{
                  padding: "1rem",
                  background: "#f8fafc",
                  borderRadius: "10px",
                  border: "1px solid #e2e8f0",
                }}
              >
                <h4
                  style={{
                    margin: "0 0 0.65rem 0",
                    fontSize: "0.85rem",
                    fontWeight: 800,
                    color: "#15803d",
                  }}
                >
                  Course Quiz (Single Choice MCQ)
                </h4>

                <div style={{ marginBottom: "0.5rem" }}>
                  <label
                    style={{
                      display: "block",
                      fontSize: "0.78rem",
                      fontWeight: 700,
                      marginBottom: "0.25rem",
                      color: "#334155",
                    }}
                  >
                    Question 1 Text *
                  </label>
                  <input
                    required
                    type="text"
                    value={courseForm.quizQuestion}
                    onChange={(e) =>
                      setCourseForm({ ...courseForm, quizQuestion: e.target.value })
                    }
                    style={{
                      width: "100%",
                      padding: "0.48rem 0.65rem",
                      borderRadius: "6px",
                      border: "1.5px solid #cbd5e1",
                      fontSize: "0.82rem",
                      background: "#ffffff",
                    }}
                  />
                </div>

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: "0.5rem",
                    marginBottom: "0.5rem",
                  }}
                >
                  {[
                    { key: "quizOption1", label: "Option A" },
                    { key: "quizOption2", label: "Option B" },
                    { key: "quizOption3", label: "Option C" },
                    { key: "quizOption4", label: "Option D" },
                  ].map(({ key, label }) => (
                    <input
                      key={key}
                      placeholder={label}
                      value={courseForm[key]}
                      onChange={(e) =>
                        setCourseForm({ ...courseForm, [key]: e.target.value })
                      }
                      style={{
                        padding: "0.45rem 0.6rem",
                        borderRadius: "6px",
                        border: "1.5px solid #cbd5e1",
                        fontSize: "0.8rem",
                        background: "#ffffff",
                      }}
                    />
                  ))}
                </div>

                <div>
                  <label
                    style={{
                      fontSize: "0.78rem",
                      fontWeight: 700,
                      marginRight: "0.5rem",
                      color: "#334155",
                    }}
                  >
                    Correct Option:
                  </label>
                  <select
                    value={courseForm.correctOption}
                    onChange={(e) =>
                      setCourseForm({
                        ...courseForm,
                        correctOption: Number(e.target.value),
                      })
                    }
                    style={{
                      padding: "0.35rem 0.65rem",
                      borderRadius: "6px",
                      border: "1.5px solid #cbd5e1",
                      fontSize: "0.8rem",
                      background: "#ffffff",
                    }}
                  >
                    <option value={0}>Option A</option>
                    <option value={1}>Option B</option>
                    <option value={2}>Option C</option>
                    <option value={3}>Option D</option>
                  </select>
                </div>
              </div>

              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: "0.75rem",
                  marginTop: "0.5rem",
                }}
              >
                <button
                  type="button"
                  className="btn-outline-action"
                  onClick={() => setShowCreateModal(false)}
                >
                  Cancel
                </button>
                <button type="submit" className="btn-green-primary">
                  Publish Course
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
